// The Engineering page's client (module 10 U89). Its own fetch helper and its own types, as Usage
// has: the page is the super admin's and nothing else in the portal reads these shapes. They mirror
// apps/api/src/engineering/view.ts and routes/engineering.ts.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';
import type { Period } from './period.js';

/** The period is a first and a last day (U107); the four names are the first build's and the server still answers them. */
export type EngineeringWindow = 'this-week' | 'last-week' | '4-weeks' | '12-weeks' | 'range';
export interface Window { key: EngineeringWindow; since: string; until: string; from: string; to: string; workingDays: number; weeks: string[] }
export interface DeveloperRow { name: string; role: string; product: string | null; leadOf: string | null; code: number; daysWorked: number; codeShare: number | null; allFiles: number | null; allFilesShare: number | null; codePerDay: number | null; largestRequest: number; pullRequests: number; approvals: number; inOthersRequests: number; pushedDirect: number; open: number | null; oldestOpenDays: number | null; wentBack: number }
export interface ProductGiven { product: string; raised: number; byProductPeople: number; byQa: number; unassigned: number; started: number; resolved: number; queueChange: number; waitingNow: number | null; medianWaitDays: number | null }
export interface ProductBack { product: string; backFromQa: number; backFromReview: number; reopened: number; bugs: number; bugsByProductPeople: number }
export interface QaRow { name: string; role: string; tested: number; passedOn: number; returned: number; bugs: number; statusChanges: number }
export interface GivenPersonRow { name: string; product: string | null; started: number | null; resolved: number | null; open: number | null; oldestOpenDays: number | null }
export interface ProductPersonRow { name: string; role: string; written: number; withDescription: number | null; bugs: number; started: number; closed: number; movedBack: number }
export interface SourceRead { source: 'bitbucket' | 'jira' | 'own'; readAt: string; lastGoodAt: string | null; error: string | null; unread: number; requestsSent: number | null }
// ---- the three QA and DevOps sections (B1522 to B1524) --------------------------------------
// These mirror apps/api/src/engineering/qa-view.ts. A null is a null the whole way to the screen:
// `items` is null where a changeset's count could not be read and a range is null where one sync is
// held, and neither may be drawn as 0 (B1466).
export interface TestedRow {
  /** The source's own name, or its key where no run of it has carried a name yet (migration 168). */
  project: string; projectKey: string; product: string | null; runs: number; results: number;
  passed: number; failed: number; blocked: number; untested: number;
  /** Skipped and Automation Error: in the total, in no other column. Drawn, never buried. */
  neither: number;
  automated: number; manual: number; withIssue: number;
}
/** One branch of the Cypress suite. A branch and not a project: this organisation's four codebases
 *  share ONE Cypress Cloud project and the branch is all that tells them apart (B1537). */
export interface SuiteRow {
  branch: string; product: string | null;
  runs: number; runsPassed: number; runsFailed: number;
  testsPassed: number; testsFailed: number; testsOther: number;
  failingSpecs: { spec: string; tests: number }[];
  /** A median of each day's median run, not a median over runs. Null where no day carried one. */
  typicalDayMedianSeconds: number | null;
  durationMaxSeconds: number | null;
  days: number;
}
export interface MirrorRow {
  repository: string; organisation: string | null; syncs: number;
  firstChangeset: number | null; lastChangeset: number | null; changesetsBetweenSyncs: number | null;
  firstSeen: string | null; lastSeen: string | null; lastMirroredOn: string | null;
}
export interface OwnWorkRow { repository: string; changesets: number; items: number | null; lastAt: string | null }
export interface MeetingPersonRow { name: string; role: string; committed: number; own: number; byKind: { kind: string; minutes: number; events: number }[]; days: number }
export interface MeetingRoleRow { role: string; people: number; committed: number; own: number; medianCommitted: number }
export interface QaSourceRead { source: string; readAt: string | null; error: string | null; lastGoodAt: string | null }
export interface Platforms {
  tested: TestedRow[];
  heldUp: { blocked: number; untested: number; projects: string[] };
  automatedSeen: boolean;
  suite: SuiteRow[];
  mirror: MirrorRow[];
  ownWork: OwnWorkRow[];
  mirrorPeople: { name: string; basis: string; repositories: number; syncs: number; changesetsBetweenSyncs: number | null }[];
  meetingPeople: MeetingPersonRow[];
  meetingRoles: MeetingRoleRow[];
  noCalendar: string[];
  meetingDaysCovered: number;
  reads: QaSourceRead[];
}

export interface EngineeringView {
  window: Window;
  lead: { code: number; allFiles: number; people: number; daysWorked: number; pullRequests: number; directCommits: number; raised: number; resolved: number; unassigned: number; wentBack: number; reopened: number; bugs: number };
  developers: DeveloperRow[];
  givenPeople: GivenPersonRow[];
  nothingRecorded: string[];
  given: ProductGiven[]; cameBack: ProductBack[];
  qa: QaRow[]; productPeople: ProductPersonRow[];
  unmappedStatuses: string[];
  read: { sources: SourceRead[]; weeksWanted: number; weeksMissing: string[]; ownAsOf: string | null; readBackTo: string | null; weeksHeld: number; ownFrom: string | null; ownTo: string | null };
  platforms: Platforms;
  store: { tenantId: string; name: string };
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (typeof b === 'object' && b !== null && typeof (b as { error?: unknown }).error === 'string' ? (b as { error: string }).error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

export const engineering = {
  read: async (period: Period): Promise<EngineeringView> => json<EngineeringView>(await fetch(`/api/engineering?from=${encodeURIComponent(period.from)}&to=${encodeURIComponent(period.to)}`, { credentials: 'same-origin' })),
};
